/**
 * Weapon sound designs for Counter Pump.
 *
 * Each of the 19 weapons gets a unique, multi-layered sound profile
 * designed to feel like a CS:GO-style tactical shooter.
 *
 * Categories:
 * - Pistols (6): short, snappy, 50-100ms
 * - Rifles (7): layered, medium, 80-150ms
 * - SMGs (3): fast, light, 30-60ms
 * - Snipers (2): heavy, echoing, 150-500ms
 * - Shotgun (1): wide spread, booming
 */

import type { SoundDesign, SoundLayer } from './sound-designer';

const SR = 44100;

function osc(
  freq: number,
  waveform: OscillatorType,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'oscillator', waveform, frequency: freq,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function noise(
  noiseType: 'white' | 'pink' | 'brown',
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'noise', noiseType, attack, decay, sustain, release, gain, ...opts,
  };
}

function fnoise(
  noiseType: 'white' | 'pink' | 'brown',
  filterType: BiquadFilterType,
  filterFreq: number,
  filterQ: number,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'filtered-noise', noiseType, filterType, filterFreq, filterQ,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function impulse(
  gain: number,
  duration: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'impulse', attack: 0, decay: 0, sustain: 1, release: 0,
    gain, duration, ...opts,
  };
}

export function getWeaponSoundDesigns(): Map<string, SoundDesign> {
  const designs = new Map<string, SoundDesign>();

  // ─── PISTOLS ────────────────────────────────────────────────────────

  designs.set('glock_fire', {
    name: 'glock_fire',
    duration: 0.075,
    sampleRate: SR,
    layers: [
      impulse(0.7, 0.005),
      osc(600, 'square', 0.35, 0.001, 0.01, 0.2, 0.03),
      osc(150, 'sine', 0.25, 0.002, 0.02, 0.1, 0.02),
      fnoise('white', 'bandpass', 3000, 2, 0.2, 0.001, 0.01, 0.1, 0.03),
    ],
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.01 },
  });

  designs.set('usp_fire', {
    name: 'usp_fire',
    duration: 0.065,
    sampleRate: SR,
    layers: [
      osc(200, 'sine', 0.3, 0.003, 0.015, 0.15, 0.02),
      fnoise('pink', 'lowpass', 1500, 1, 0.25, 0.002, 0.01, 0.15, 0.02),
      osc(400, 'triangle', 0.15, 0.002, 0.01, 0.1, 0.02),
    ],
    lowpass: 4000,
    compression: { threshold: 0.4, ratio: 3, attack: 0.001, release: 0.01 },
  });

  designs.set('p250_fire', {
    name: 'p250_fire',
    duration: 0.07,
    sampleRate: SR,
    layers: [
      impulse(0.75, 0.004),
      osc(700, 'square', 0.35, 0.001, 0.012, 0.15, 0.025),
      osc(180, 'sine', 0.28, 0.002, 0.015, 0.12, 0.02),
      fnoise('white', 'bandpass', 3500, 2, 0.2, 0.001, 0.01, 0.1, 0.02),
    ],
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.01 },
  });

  designs.set('deagle_fire', {
    name: 'deagle_fire',
    duration: 0.18,
    sampleRate: SR,
    layers: [
      impulse(0.85, 0.006),
      osc(60, 'sine', 0.55, 0.003, 0.04, 0.15, 0.06),
      osc(120, 'sawtooth', 0.35, 0.002, 0.03, 0.1, 0.05),
      osc(800, 'square', 0.3, 0.001, 0.008, 0.05, 0.02),
      fnoise('white', 'bandpass', 2000, 1.5, 0.25, 0.001, 0.02, 0.08, 0.06),
      noise('brown', 0.15, 0.005, 0.03, 0.05, 0.06),
    ],
    compression: { threshold: 0.4, ratio: 4, attack: 0.001, release: 0.02 },
    distortion: { amount: 1.3, oversample: 'none' },
  });

  designs.set('fiveseven_fire', {
    name: 'fiveseven_fire',
    duration: 0.065,
    sampleRate: SR,
    layers: [
      impulse(0.7, 0.004),
      osc(800, 'square', 0.3, 0.001, 0.01, 0.15, 0.02),
      osc(200, 'sine', 0.2, 0.002, 0.012, 0.1, 0.02),
      fnoise('white', 'highpass', 4000, 1.5, 0.18, 0.001, 0.008, 0.1, 0.02),
    ],
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.01 },
  });

  designs.set('tec9_fire', {
    name: 'tec9_fire',
    duration: 0.06,
    sampleRate: SR,
    layers: [
      impulse(0.65, 0.005),
      osc(350, 'sawtooth', 0.35, 0.001, 0.01, 0.15, 0.02),
      osc(700, 'sawtooth', 0.2, 0.001, 0.008, 0.1, 0.015),
      fnoise('white', 'bandpass', 2500, 2, 0.22, 0.001, 0.01, 0.1, 0.02),
    ],
    distortion: { amount: 1.2, oversample: 'none' },
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.01 },
  });

  // ─── RIFLES ─────────────────────────────────────────────────────────

  designs.set('ak47_fire', {
    name: 'ak47_fire',
    duration: 0.13,
    sampleRate: SR,
    layers: [
      impulse(0.8, 0.005),
      osc(60, 'sine', 0.45, 0.002, 0.03, 0.1, 0.04),
      osc(800, 'square', 0.35, 0.001, 0.005, 0.05, 0.015),
      fnoise('pink', 'bandpass', 2000, 1.5, 0.3, 0.001, 0.015, 0.1, 0.04),
      noise('brown', 0.12, 0.005, 0.02, 0.05, 0.04),
      osc(250, 'sawtooth', 0.15, 0.002, 0.01, 0.05, 0.03),
    ],
    compression: { threshold: 0.4, ratio: 4, attack: 0.001, release: 0.02 },
    distortion: { amount: 1.2, oversample: 'none' },
  });

  designs.set('m4a4_fire', {
    name: 'm4a4_fire',
    duration: 0.1,
    sampleRate: SR,
    layers: [
      impulse(0.75, 0.004),
      osc(100, 'sine', 0.3, 0.002, 0.02, 0.1, 0.03),
      osc(1000, 'square', 0.35, 0.001, 0.005, 0.08, 0.015),
      fnoise('white', 'bandpass', 3000, 2, 0.25, 0.001, 0.01, 0.1, 0.03),
      osc(500, 'triangle', 0.12, 0.002, 0.008, 0.05, 0.02),
    ],
    compression: { threshold: 0.45, ratio: 3.5, attack: 0.001, release: 0.015 },
  });

  designs.set('m4a1s_fire', {
    name: 'm4a1s_fire',
    duration: 0.085,
    sampleRate: SR,
    layers: [
      osc(200, 'sine', 0.3, 0.003, 0.02, 0.15, 0.02),
      osc(400, 'triangle', 0.15, 0.002, 0.01, 0.1, 0.02),
      fnoise('pink', 'bandpass', 2000, 1.5, 0.3, 0.002, 0.015, 0.15, 0.025),
      fnoise('white', 'lowpass', 1800, 1, 0.15, 0.003, 0.02, 0.1, 0.02),
    ],
    lowpass: 5000,
    compression: { threshold: 0.4, ratio: 3, attack: 0.002, release: 0.015 },
  });

  designs.set('famas_fire', {
    name: 'famas_fire',
    duration: 0.09,
    sampleRate: SR,
    layers: [
      impulse(0.7, 0.004),
      osc(120, 'sine', 0.25, 0.002, 0.015, 0.1, 0.025),
      osc(900, 'square', 0.3, 0.001, 0.006, 0.08, 0.015),
      fnoise('white', 'bandpass', 3500, 2, 0.2, 0.001, 0.01, 0.08, 0.025),
    ],
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.012 },
  });

  designs.set('galil_fire', {
    name: 'galil_fire',
    duration: 0.11,
    sampleRate: SR,
    layers: [
      impulse(0.72, 0.005),
      osc(80, 'sine', 0.35, 0.002, 0.025, 0.1, 0.035),
      osc(750, 'square', 0.3, 0.001, 0.006, 0.06, 0.015),
      fnoise('pink', 'bandpass', 2200, 1.5, 0.22, 0.001, 0.012, 0.08, 0.035),
      noise('brown', 0.1, 0.004, 0.015, 0.04, 0.035),
    ],
    compression: { threshold: 0.45, ratio: 3.5, attack: 0.001, release: 0.018 },
  });

  designs.set('aug_fire', {
    name: 'aug_fire',
    duration: 0.095,
    sampleRate: SR,
    layers: [
      impulse(0.7, 0.004),
      osc(110, 'sine', 0.28, 0.002, 0.018, 0.1, 0.03),
      osc(950, 'square', 0.3, 0.001, 0.005, 0.07, 0.015),
      fnoise('white', 'bandpass', 3200, 2, 0.22, 0.001, 0.01, 0.08, 0.025),
      osc(450, 'triangle', 0.1, 0.002, 0.008, 0.05, 0.02),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.015 },
  });

  designs.set('sg553_fire', {
    name: 'sg553_fire',
    duration: 0.1,
    sampleRate: SR,
    layers: [
      impulse(0.73, 0.005),
      osc(90, 'sine', 0.32, 0.002, 0.02, 0.1, 0.035),
      osc(850, 'square', 0.32, 0.001, 0.006, 0.07, 0.015),
      fnoise('pink', 'bandpass', 2800, 1.8, 0.24, 0.001, 0.012, 0.08, 0.03),
      osc(350, 'sawtooth', 0.1, 0.002, 0.008, 0.04, 0.025),
    ],
    compression: { threshold: 0.45, ratio: 3.5, attack: 0.001, release: 0.016 },
  });

  // ─── SNIPERS ────────────────────────────────────────────────────────

  designs.set('awp_fire', {
    name: 'awp_fire',
    duration: 0.5,
    sampleRate: SR,
    layers: [
      impulse(0.95, 0.008),
      osc(30, 'sine', 0.6, 0.003, 0.08, 0.1, 0.2),
      osc(80, 'sine', 0.4, 0.002, 0.05, 0.08, 0.15),
      osc(2000, 'square', 0.35, 0.001, 0.003, 0.02, 0.01, { duration: 0.02 }),
      osc(400, 'sawtooth', 0.25, 0.002, 0.03, 0.05, 0.1),
      fnoise('pink', 'bandpass', 1500, 1, 0.3, 0.002, 0.03, 0.1, 0.15),
      noise('brown', 0.2, 0.01, 0.05, 0.08, 0.2),
      fnoise('white', 'highpass', 5000, 2, 0.1, 0.001, 0.005, 0.02, 0.05,
        { startTime: 0.0, duration: 0.05 }),
    ],
    compression: { threshold: 0.35, ratio: 5, attack: 0.001, release: 0.03 },
    distortion: { amount: 1.4, oversample: 'none' },
  });

  designs.set('ssg08_fire', {
    name: 'ssg08_fire',
    duration: 0.25,
    sampleRate: SR,
    layers: [
      impulse(0.85, 0.006),
      osc(50, 'sine', 0.4, 0.003, 0.04, 0.08, 0.1),
      osc(1200, 'square', 0.35, 0.001, 0.004, 0.04, 0.015),
      osc(300, 'sawtooth', 0.2, 0.002, 0.02, 0.06, 0.08),
      fnoise('pink', 'bandpass', 2000, 1.5, 0.22, 0.002, 0.02, 0.08, 0.1),
      noise('brown', 0.12, 0.005, 0.03, 0.05, 0.08),
    ],
    compression: { threshold: 0.4, ratio: 4, attack: 0.001, release: 0.025 },
    distortion: { amount: 1.2, oversample: 'none' },
  });

  // ─── SMGs ───────────────────────────────────────────────────────────

  designs.set('mp9_fire', {
    name: 'mp9_fire',
    duration: 0.045,
    sampleRate: SR,
    layers: [
      impulse(0.6, 0.003),
      osc(500, 'square', 0.3, 0.001, 0.008, 0.1, 0.015),
      osc(150, 'sine', 0.2, 0.002, 0.01, 0.08, 0.012),
      fnoise('white', 'bandpass', 4000, 2, 0.18, 0.001, 0.008, 0.08, 0.012),
    ],
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.008 },
  });

  designs.set('mac10_fire', {
    name: 'mac10_fire',
    duration: 0.05,
    sampleRate: SR,
    layers: [
      impulse(0.65, 0.004),
      osc(300, 'sawtooth', 0.35, 0.001, 0.01, 0.12, 0.015),
      osc(600, 'sawtooth', 0.2, 0.001, 0.008, 0.08, 0.012),
      fnoise('white', 'bandpass', 2500, 1.5, 0.22, 0.001, 0.008, 0.1, 0.015),
    ],
    distortion: { amount: 1.3, oversample: 'none' },
    compression: { threshold: 0.45, ratio: 3.5, attack: 0.001, release: 0.008 },
  });

  designs.set('p90_fire', {
    name: 'p90_fire',
    duration: 0.04,
    sampleRate: SR,
    layers: [
      impulse(0.55, 0.003),
      osc(400, 'triangle', 0.28, 0.001, 0.008, 0.1, 0.012),
      osc(180, 'sine', 0.22, 0.002, 0.01, 0.08, 0.01),
      fnoise('pink', 'lowpass', 3000, 1.5, 0.2, 0.001, 0.008, 0.08, 0.01),
    ],
    lowpass: 6000,
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.008 },
  });

  // ─── SHOTGUN ────────────────────────────────────────────────────────

  designs.set('nova_fire', {
    name: 'nova_fire',
    duration: 0.22,
    sampleRate: SR,
    layers: [
      impulse(0.9, 0.008),
      osc(50, 'sine', 0.5, 0.003, 0.05, 0.1, 0.08),
      osc(150, 'sawtooth', 0.3, 0.002, 0.03, 0.08, 0.06),
      noise('white', 0.35, 0.001, 0.02, 0.08, 0.08),
      fnoise('pink', 'bandpass', 1500, 1, 0.25, 0.002, 0.025, 0.06, 0.07),
      fnoise('white', 'highpass', 3000, 2, 0.15, 0.001, 0.008, 0.04, 0.03,
        { startTime: 0.0, duration: 0.06 }),
      noise('brown', 0.12, 0.005, 0.03, 0.05, 0.08),
    ],
    compression: { threshold: 0.35, ratio: 4.5, attack: 0.001, release: 0.025 },
    distortion: { amount: 1.3, oversample: 'none' },
  });

  return designs;
}
